import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { getProductPages, isOutsideFlag, resolveSpecImage, type ProductSpecItem } from '@/api/product';

const PAGE_SIZE = 20;

export interface SelectableProduct {
  /** 规格 ID */
  id: string;
  goodsId: string;
  name: string;
  code: string;
  price: number;
  unit: string;
  model: string;
  stock: number;
  image: string;
  /** 是否清单外规格 */
  isOutside: boolean;
}

interface ProductSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (product: SelectableProduct) => void;
  /** 已存在的规格 ID，用于标记/禁止重复添加 */
  existingProductIds: string[];
}

function mapSelectableProduct(item: ProductSpecItem): SelectableProduct {
  const price = Number(item.price ?? 0);
  const stock = typeof item.stock === 'number' ? item.stock : Number(item.stock ?? 0);
  return {
    id: String(item.id),
    goodsId: String(item.goods_id || item.goods?.id || item.id),
    name: item.goods?.title || '未命名物资',
    code: item.goods?.code || '',
    price: Number.isFinite(price) ? price : 0,
    unit: item.unit || '件',
    model: item.name || '',
    stock: Number.isFinite(stock) ? stock : 0,
    image: resolveSpecImage(item.pic, item.goods?.m_pic),
    isOutside: isOutsideFlag(item.is_outside),
  };
}

export default function ProductSelector({
  isOpen,
  onClose,
  onSelect,
  existingProductIds,
}: ProductSelectorProps) {
  const [searchInput, setSearchInput] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [products, setProducts] = useState<SelectableProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const loadingLockRef = useRef(false);
  const filterGenRef = useRef(0);
  const searchKeywordRef = useRef('');

  const existingSet = useMemo(
    () => new Set(existingProductIds.map(String).filter(Boolean)),
    [existingProductIds],
  );

  // 已在清单中的仍展示（禁用），避免搜索命中后被滤空误显示「未找到」
  const selectableProducts = useMemo(
    () => products.filter((p) => !existingSet.has(p.id)),
    [products, existingSet],
  );
  const existingInResultCount = useMemo(
    () => products.filter((p) => existingSet.has(p.id)).length,
    [products, existingSet],
  );

  const hasMore = products.length < total;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchProducts = useCallback(async (pageNum: number, mode: 'replace' | 'append', gen: number) => {
    if (mode === 'append' && loadingLockRef.current) return;
    loadingLockRef.current = true;

    if (mode === 'replace') setLoading(true);
    else setLoadingMore(true);
    setError('');

    try {
      const res = await getProductPages({
        page: pageNum,
        pagenumber: PAGE_SIZE,
        search: searchKeywordRef.current.trim() || undefined,
        // 采购/下单选物资：按更新时间，避免默认销量排序建临时表拖慢搜索
        sortBy: 'new',
      });

      if (gen !== filterGenRef.current) return;

      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        setError(res.message || res.msg || '物资加载失败');
        if (mode === 'replace') {
          setProducts([]);
          setTotal(0);
        }
        return;
      }

      const list = Array.isArray(res.data) ? res.data.map(mapSelectableProduct) : [];
      setTotal(Number(res.total ?? 0) || 0);
      setProducts((prev) => (mode === 'replace' ? list : [...prev, ...list]));
    } catch {
      if (gen !== filterGenRef.current) return;
      setError('物资加载失败，请稍后重试');
      if (mode === 'replace') {
        setProducts([]);
        setTotal(0);
      }
    } finally {
      if (gen === filterGenRef.current) {
        loadingLockRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      } else {
        loadingLockRef.current = false;
      }
    }
  }, []);

  const reloadFirstPage = useCallback(() => {
    const gen = ++filterGenRef.current;
    loadingLockRef.current = false;
    setPage(1);
    setProducts([]);
    setTotal(0);
    setSelectedIndex(0);
    void fetchProducts(1, 'replace', gen);
  }, [fetchProducts]);

  useEffect(() => {
    if (!isOpen) return;
    setSearchInput('');
    searchKeywordRef.current = '';
    setError('');
    reloadFirstPage();
    const timer = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(timer);
  }, [isOpen, reloadFirstPage]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = window.setTimeout(() => {
      const next = searchInput.trim();
      if (next === searchKeywordRef.current) return;
      searchKeywordRef.current = next;
      reloadFirstPage();
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput, isOpen, reloadFirstPage]);

  useEffect(() => {
    if (!isOpen || page <= 1) return;
    void fetchProducts(page, 'append', filterGenRef.current);
  }, [isOpen, page, fetchProducts]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [products.length, searchInput]);

  const loadNextPage = useCallback(() => {
    if (loadingLockRef.current || loading || loadingMore || !hasMore || error) return;
    if (page >= totalPages) return;
    setPage((p) => p + 1);
  }, [loading, loadingMore, hasMore, error, page, totalPages]);

  useEffect(() => {
    if (!isOpen) return;
    const target = loadMoreRef.current;
    const root = listRef.current;
    if (!target || !root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadNextPage();
        }
      },
      { root, rootMargin: '80px 0px', threshold: 0 },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [isOpen, loadNextPage, products.length, hasMore]);

  const resetAndClose = useCallback(() => {
    setSearchInput('');
    searchKeywordRef.current = '';
    onClose();
  }, [onClose]);

  const handleSelect = useCallback(
    (product: SelectableProduct) => {
      if (existingSet.has(product.id)) return;
      onSelect(product);
      resetAndClose();
    },
    [existingSet, onSelect, resetAndClose],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, Math.max(products.length - 1, 0)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const current = products[selectedIndex];
        if (current && !existingSet.has(current.id)) {
          handleSelect(current);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        resetAndClose();
      }
    },
    [products, selectedIndex, existingSet, handleSelect, resetAndClose],
  );

  useEffect(() => {
    if (!isOpen) return;
    const handleGlobalKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        resetAndClose();
      }
    };
    window.addEventListener('keydown', handleGlobalKey);
    return () => window.removeEventListener('keydown', handleGlobalKey);
  }, [isOpen, resetAndClose]);

  useEffect(() => {
    if (!listRef.current || products.length === 0) return;
    const selectedEl = listRef.current.querySelector(
      `[data-product-index="${selectedIndex}"]`,
    ) as HTMLElement | null;
    selectedEl?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex, products.length]);

  if (!isOpen) return null;

  const showInitialLoading = loading && products.length === 0;
  const showEmpty = !showInitialLoading && !error && products.length === 0;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) resetAndClose();
      }}
    >
      <div className="bg-background-50 rounded-xl w-full max-w-2xl h-[90vh] flex flex-col shadow-2xl m-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 shrink-0">
          <h3 className="text-sm font-semibold text-foreground-900">选择物资</h3>
          <button
            type="button"
            onClick={resetAndClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-background-100 shrink-0">
          <div className="flex items-center gap-2 px-3 py-2 border border-background-300 rounded-lg bg-background-100">
            <i className="ri-search-line text-foreground-400" />
            <input
              ref={inputRef}
              type="text"
              placeholder="输入物资名称、规格编码搜索，回车选中"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 text-sm bg-transparent outline-none text-foreground-900 placeholder:text-foreground-400"
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput('')}
                className="text-foreground-400 hover:text-foreground-600"
              >
                <i className="ri-close-line" />
              </button>
            )}
          </div>
          <p className="text-[10px] text-foreground-400 mt-1.5">
            支持键盘 ↑ ↓ 选择，Enter 确认，Esc 关闭
            {total > 0 ? ` · 共 ${total} 条` : ''}
            {existingInResultCount > 0 ? ` · 其中 ${existingInResultCount} 条已在清单中` : ''}
            {selectableProducts.length === 0 && existingInResultCount > 0
              ? '（请勿重复添加）'
              : ''}
          </p>
        </div>

        <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto px-2 py-2">
          {showInitialLoading ? (
            <div className="h-full flex flex-col items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin text-xl text-primary-500" />
              <p className="text-sm text-foreground-400">正在加载物资...</p>
            </div>
          ) : error ? (
            <div className="h-full flex flex-col items-center justify-center gap-2">
              <p className="text-sm text-foreground-500">{error}</p>
              <button
                type="button"
                onClick={reloadFirstPage}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                重新加载
              </button>
            </div>
          ) : showEmpty ? (
            <div className="h-full flex items-center justify-center text-sm text-foreground-400">
              未找到匹配的物资
            </div>
          ) : (
            <>
              {products.map((product, i) => {
                const alreadyInList = existingSet.has(product.id);
                return (
                  <div
                    key={product.id}
                    data-product-index={i}
                    onClick={() => handleSelect(product)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      alreadyInList
                        ? 'opacity-55 cursor-not-allowed bg-background-100'
                        : i === selectedIndex
                          ? 'bg-primary-50 ring-1 ring-primary-200 cursor-pointer'
                          : 'hover:bg-background-100 cursor-pointer'
                    }`}
                  >
                    <div className="w-12 h-12 rounded-md overflow-hidden shrink-0 bg-background-100">
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-foreground-300 text-lg" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="text-sm font-medium text-foreground-900 truncate">{product.name}</p>
                        {alreadyInList && (
                          <span className="px-2 py-0.5 bg-background-300 text-foreground-600 text-xs rounded font-medium shrink-0">
                            已在清单
                          </span>
                        )}
                        {product.isOutside && (
                          <span className="px-2 py-0.5 bg-orange-500 text-white text-xs rounded font-medium shrink-0">
                            清单外
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-foreground-500 mt-0.5 truncate">
                        {product.model || product.code || '-'}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-foreground-500">单位：{product.unit}</span>
                        <span className="text-xs font-medium text-primary-600">
                          ¥{product.price.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="shrink-0">
                      {!alreadyInList && i === selectedIndex && (
                        <span className="text-xs text-primary-600 font-medium">↵ 选中</span>
                      )}
                    </div>
                  </div>
                );
              })}

              <div ref={loadMoreRef} className="py-3 flex flex-col items-center justify-center gap-1">
                {loadingMore && (
                  <>
                    <i className="ri-loader-4-line animate-spin text-primary-500" />
                    <span className="text-xs text-foreground-400">加载更多...</span>
                  </>
                )}
                {!loadingMore && hasMore && products.length > 0 && (
                  <span className="text-xs text-foreground-400">
                    下拉加载更多（{page}/{totalPages}）
                  </span>
                )}
                {!loadingMore && !hasMore && products.length > 0 && (
                  <span className="text-xs text-foreground-400">已加载全部物资</span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
